import { forwardRef } from 'react';
import { cn } from '../../../lib/cn';
import { FilterGlyph } from './icons';

/**
 * ResultBar.
 *
 * CSS selector: `organisms.css .ds-resultbar`, `--pinned`, `__count`.
 *
 * Doctrine:
 *
 * - Sticky while the grid scrolls under it, and it only grows a hairline once
 *   it has actually stuck. `pinned` is a scroll observation from the page, not
 *   a style choice, so drive it from an IntersectionObserver on a sentinel.
 * - The count leads in ink and the qualifier trails in a lighter `<small>`,
 *   which is the same darker label, lighter detail invert used on every card.
 * - `aria-live="polite"` so a filter change is announced without stealing
 *   focus. The bar reports, it does not interrupt.
 * - Note the app level ambiguity this bar renders: `available` counts listings
 *   on the marketplace but sums quantity on a seller page. Pass the already
 *   resolved number, do not compute it here.
 */
export const ResultBar = forwardRef(function ResultBar(
  { className, pinned = false, count, countLabel, bleed, trailing, style, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn('ds-resultbar', pinned && 'ds-resultbar--pinned', className)}
      style={bleed ? { '--ds-bleed': bleed, ...style } : style}
      {...rest}
    >
      <p className="ds-resultbar__count" aria-live="polite">
        {count} {countLabel && <small>{countLabel}</small>}
      </p>
      {trailing}
    </div>
  );
});

/**
 * FilterButton.
 *
 * CSS selector: `organisms.css .ds-filterbtn` and `__count`.
 *
 * Doctrine: the applied count pill is INK, not accent, because it reports a
 * command the user issued rather than a state the system is in. That is the
 * deliberate opposite of the tab bar's accent cart badge. The button itself is
 * an outlined pill on `--bg-primary`, never accent filled.
 *
 * The count is folded into the accessible name so a screen reader hears
 * "Filters, 3 applied" rather than an unattached number.
 */
export const FilterButton = forwardRef(function FilterButton(
  { className, count, icon = true, children = 'Filters', ...rest },
  ref,
) {
  const applied = typeof count === 'number' && count > 0;

  return (
    <button
      ref={ref}
      type="button"
      className={cn('ds-filterbtn', className)}
      aria-label={applied ? `${children}, ${count} applied` : undefined}
      {...rest}
    >
      {icon && <FilterGlyph />}
      {children}
      {applied && (
        <span className="ds-filterbtn__count" aria-hidden="true">
          {count}
        </span>
      )}
    </button>
  );
});

export default ResultBar;
